import { supabase } from "~/integrations/supabase/client";

/**
 * Interface for payment verification calculation results
 */
export interface PaymentVerificationResult {
  verifiedAmount: number;
  totalPaid: number;
  unverifiedPayments: Array<{
    id: number;
    amount: number;
    payment_method: string;
    is_verified: boolean | null;
  }>;
}

/**
 * Interface for payment data from database
 */
interface PaymentData {
  id: number;
  amount: number;
  payment_method: string;
  is_verified: boolean | null;
  check_status: string | null;
}

/**
 * Calculate verified payment amounts for a sales request
 *
 * @param salesRequestId - The UUID of the sales request
 * @returns Promise with verification calculation results
 */
export async function calculateVerifiedPayments(
  salesRequestId: string
): Promise<PaymentVerificationResult> {
  const { data: payments, error } = await supabase
    .from("payments")
    .select("id, amount, payment_method, is_verified, check_status")
    .eq("sales_request", salesRequestId);

  if (error) {
    console.error("Error fetching payments for verification:", error);
    throw new Error(`Failed to fetch payments: ${error.message}`);
  }

  if (!payments) {
    return {
      verifiedAmount: 0,
      totalPaid: 0,
      unverifiedPayments: [],
    };
  }

  const verifiedAmount = payments.reduce((sum, payment: PaymentData) => {
    // For check payments, use check_status instead of is_verified
    if (payment.payment_method === "check") {
      // Only count collected or paid checks as verified
      return payment.check_status === "collected" ||
        payment.check_status === "paid"
        ? sum + payment.amount
        : sum;
    }

    // For all other payments, use is_verified boolean
    return payment.is_verified === true ? sum + payment.amount : sum;
  }, 0);

  const totalPaid = payments.reduce((sum, payment: PaymentData) => {
    return sum + payment.amount;
  }, 0);

  const unverifiedPayments = payments
    .filter((payment: PaymentData) => {
      // Exclude checks from unverified list (they have their own workflow)
      if (payment.payment_method === "check") {
        return false;
      }
      // Include only payments that are explicitly false (not verified)
      return payment.is_verified === false;
    })
    .map((payment: PaymentData) => ({
      id: payment.id,
      amount: payment.amount,
      payment_method: payment.payment_method,
      is_verified: payment.is_verified,
    }));

  return {
    verifiedAmount,
    totalPaid,
    unverifiedPayments,
  };
}

/**
 * Verify all unverified payments for a sales request
 * This is used by the "Marcar como pagado" functionality
 *
 * @param salesRequestId - The UUID of the sales request
 * @param verifiedBy - The UUID of the user performing the verification
 * @returns Promise with the number of payments verified
 */
export async function verifyAllUnverifiedPayments(
  salesRequestId: string,
  verifiedBy: string
): Promise<number> {
  // First, get payments that need verification (false or null is_verified, excluding checks)
  const { data: paymentsToUpdate } = await supabase
    .from("payments")
    .select("id")
    .eq("sales_request", salesRequestId)
    .neq("payment_method", "check")
    .or("is_verified.eq.false,is_verified.is.null");

  if (!paymentsToUpdate || paymentsToUpdate.length === 0) {
    return 0;
  }

  // Update each payment individually to avoid the null comparison issue
  const paymentIds = paymentsToUpdate.map((p) => p.id);

  const { data, error } = await supabase
    .from("payments")
    .update({
      is_verified: true,
      verified_by: verifiedBy,
      verified_at: new Date().toISOString(),
    })
    .in("id", paymentIds)
    .select("id");

  if (error) {
    console.error("Error verifying payments:", error);
    throw new Error(`Failed to verify payments: ${error.message}`);
  }

  return data?.length || 0;
}

/**
 * Check if a sales request should be marked as paid based on verified payments
 *
 * @param salesRequestId - The UUID of the sales request
 * @param totalAmount - The total amount due for the sales request
 * @returns Promise<boolean> - true if verified payments cover the total amount
 */
export async function shouldMarkAsPaid(
  salesRequestId: string,
  totalAmount: number
): Promise<boolean> {
  const { verifiedAmount } = await calculateVerifiedPayments(salesRequestId);

  // Round both amounts to handle decimal precision issues
  const roundedVerifiedAmount = Math.round(verifiedAmount);
  const roundedTotalAmount = Math.round(totalAmount);

  // Use tolerance of 1 peso to handle minor rounding differences
  const tolerance = 1;

  return roundedVerifiedAmount >= roundedTotalAmount - tolerance;
}
